import { defineComponent, ref, onMounted, nextTick, openBlock, createElementBlock, Fragment, renderSlot, createCommentVNode, createBlock, unref, mergeProps } from 'vue';

const DEFAULT_DOMAIN = "meet.jit.si";
const JAAS_STAGING_DOMAIN = "stage.8x8.vc";
const JAAS_PROD_DOMAIN = "8x8.vc";

//Gotten from https://github.com/jitsi/jitsi-meet-react-sdk/blob/f1e2a1c2d7fda7dfc37090b0e35252d7c404e916/src/utils/index.ts
/**
 * Returns the complete room name
 *
 * @param {string} roomName
 * @param {string|undefined} tenant
 * @returns {string} the complete room name
 */

const getRoomName = (roomName, tenant) => {
  if (tenant) {
    return `${tenant}/${roomName}`;
  }

  return roomName;
};
/**
 * Returns the JaaS domain
 *
 * @param {boolean|undefined} useStaging
 * @returns {string} the JaaS domain based on environment
 */

const getJaaSDomain = useStaging => {
  if (useStaging) {
    return JAAS_STAGING_DOMAIN;
  }

  return JAAS_PROD_DOMAIN;
};
let instancesCounter = 0;
/**
 * Generates a unique id
 * @param {string} prefix
 * @returns {string} the component id
 */

const generateComponentId = prefix => `${prefix}-${instancesCounter++}`;

const loadExternalApi = async domain => new Promise((resolve, reject) => {
  if (window.JitsiMeetExternalAPI) {
    return resolve(window.JitsiMeetExternalAPI);
  }

  const externalApiScript = document.createElement("script");
  externalApiScript.async = true;
  externalApiScript.src = `https://${domain}/external_api.js`;

  externalApiScript.onload = () => resolve(window.JitsiMeetExternalAPI);

  externalApiScript.onerror = () => reject(new Error(`Script load error: ${externalApiScript.src}`));

  document.head.appendChild(externalApiScript);
});

let scriptPromise;
/**
 * Injects the external_api.js script for the corresponding domain in DOM
 * and resolves with either the `JitsiMeetExternalApi` class definition or an error
 *
 * @param {string} domain - The domain of the external API
 * @returns {Promise<JitsiMeetExternalApi>} - the JitsiMeetExternalAPI or an error
 */

const fetchExternalApi = function () {
  let domain = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_DOMAIN;

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = loadExternalApi(domain);
  return scriptPromise;
};

const _hoisted_1 = ["id"];

var script$1 = /*#__PURE__*/defineComponent({
  __name: 'jitsi-meeting',
  props: {
    domain: {
      default: DEFAULT_DOMAIN
    },
    roomName: null,
    width: {
      default: 600
    },
    height: {
      default: 400
    },
    configOverwrite: null,
    interfaceConfigOverwrite: null,
    jwt: null,
    invitees: null,
    devices: null,
    userInfo: null,
    lang: {
      default: "en"
    }
  },
  emits: ["onApiReady", "onReadyToClose", "getIframeRefOnApiReady"],

  setup(__props, _ref) {
    let {
      emit
    } = _ref;
    const props = __props; // eslint-disable-next-line vue/no-setup-props-destructure

    const {
      domain,
      roomName,
      width,
      height,
      configOverwrite,
      interfaceConfigOverwrite,
      jwt,
      invitees,
      devices,
      userInfo,
      lang
    } = props;
    const componentId = ref("");
    const loading = ref(false);
    const apiLoaded = ref(false);
    const externalApi = ref();
    const apiRef = ref();
    const meetingRef = ref(null);
    onMounted(() => {
      componentId.value = generateComponentId("jitsiMeeting");
      fetchExternalApi(domain).then(async api => {
        externalApi.value = api;
        apiLoaded.value = true;
        await nextTick();
        loadIframe(externalApi.value);
      }).catch(e => console.error(e.message));
    });

    const loadIframe = JitsiMeetExternalAPI => {
      apiRef.value = new JitsiMeetExternalAPI(domain, {
        roomName,
        width,
        height,
        configOverwrite,
        interfaceConfigOverwrite,
        jwt,
        invitees,
        devices,
        userInfo,
        parentNode: meetingRef.value,
        lang
      });
      loading.value = false;

      if (apiRef.value) {
        emit("onApiReady", apiRef.value);
        apiRef.value.on("readyToClose", () => {
          emit("onReadyToClose");
        });

        if (meetingRef.value) {
          emit("getIframeRefOnApiReady", meetingRef.value);
        }
      }
    };

    return (_ctx, _cache) => {
      return openBlock(), createElementBlock(Fragment, null, [loading.value || !apiRef.value ? renderSlot(_ctx.$slots, "spinner", {
        key: 0
      }) : createCommentVNode("", true), (openBlock(), createElementBlock("div", {
        id: componentId.value,
        key: componentId.value,
        ref_key: "meetingRef",
        ref: meetingRef
      }, null, 8, _hoisted_1))], 64);
    };
  }

});

var script = /*#__PURE__*/defineComponent({
  __name: 'jaas-meeting',
  props: {
    appId: null,
    useStaging: {
      type: Boolean,
      default: true
    }
  },

  setup(__props) {
    const props = __props; // const emit = defineEmits<{
    //   (e: "onApiReady", externalApi: IJitsiMeetExternalApi): void;
    //   (e: "onReadyToClose"): void;
    //   (e: "getIframeRefOnApiReady", parentNode: HTMLDivElement): void;
    // }>();
    // // eslint-disable-next-line vue/no-setup-props-destructure

    const {
      appId,
      useStaging,
      roomName
    } = props;
    const jaasDomain = ref(getJaaSDomain(useStaging));
    const roomNameString = ref(getRoomName(roomName, appId));
    return (_ctx, _cache) => {
      return openBlock(), createBlock(unref(script$1), mergeProps({ ..._ctx.$props,
        ..._ctx.$attrs
      }, {
        domain: jaasDomain.value,
        "room-name": roomNameString.value
      }), null, 16, ["domain", "room-name"]);
    };
  }

});

/* eslint-disable */

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  JitsiMeeting: script$1,
  JaaSMeeting: script
});

// Import vue components

const install = function installJitsiMeetVueSdk(app) {
  Object.entries(components).forEach(_ref => {
    let [componentName, component] = _ref;
    app.component(componentName, component);
  });
}; // Create module definition for Vue.use()

export { script as JaaSMeeting, script$1 as JitsiMeeting, install as default };
